import * as Sentry from '@sentry/vue'

/**
 * Central error reporting. Sends handled/caught errors to Sentry (when initialised)
 * and always logs to the console for local visibility.
 *
 * Why this exists: the app previously swallowed errors in `catch` blocks with a bare
 * `console.error`, so Sentry only ever saw genuinely-unhandled exceptions. Route every
 * caught error through `reportError()` so it reaches Sentry with useful context.
 *
 * `Sentry.captureException` is a safe no-op when Sentry is not initialised (dev/test/CI,
 * or when VITE_SENTRY_ENABLED/DSN are unset), so this is safe to call everywhere.
 */
export interface ReportContext {
  /** Short label for where the error happened, e.g. 'PublicProposalView.validateAccess'. */
  where?: string
  /** Arbitrary structured context attached to the Sentry event. */
  extra?: Record<string, unknown>
  /** Indexed tags for filtering in Sentry. */
  tags?: Record<string, string>
  /** Sentry grouping fingerprint — events sharing it collapse into one issue. */
  fingerprint?: string[]
}

export function reportError(error: unknown, context: ReportContext = {}): void {
  const { where, extra, tags, fingerprint } = context

  // Always keep console visibility (local dev + prod console).
  if (where) console.error(`[${where}]`, error)
  else console.error(error)

  // Build the capture context with only the keys we actually have — the SDK's types use
  // exactOptionalPropertyTypes, so passing `extra: undefined`/`tags: undefined` is rejected.
  const mergedTags: Record<string, string> = { ...(tags ?? {}) }
  if (where) mergedTags.where = where

  const scope: {
    tags?: Record<string, string>
    extra?: Record<string, unknown>
    fingerprint?: string[]
  } = {}
  if (Object.keys(mergedTags).length > 0) scope.tags = mergedTags
  if (extra) scope.extra = extra
  if (fingerprint) scope.fingerprint = fingerprint

  Sentry.captureException(error, scope)
}

/**
 * Router-level error handler (wired to `router.onError` in main.ts). Reports the error
 * to Sentry, then recovers from stale-chunk failures with a one-time reload.
 *
 * `reload` is injectable for tests; production callers use the default.
 */
export function handleRouterError(
  error: unknown,
  reload: () => void = () => window.location.reload()
): void {
  if (!isChunkLoadError(error)) {
    reportError(error, { where: 'router.onError' })
    return
  }

  // Stale cached chunk after a deploy (lazy route import 404s) → reload once to fetch fresh
  // assets instead of leaving a blank page. Guarded so we never loop on a genuine error.
  // Report BEFORE reloading (the reload tears the page down) and tag the outcome so Sentry
  // can separate auto-recovered deploy noise from clients left on a broken page.
  let willReload = true
  try {
    willReload = !sessionStorage.getItem('sc-chunk-reloaded')
  } catch {
    /* sessionStorage unavailable (private mode) — reload unguarded, matching prior behavior */
  }

  reportError(error, {
    where: 'router.onError',
    tags: { chunkError: willReload ? 'recovered' : 'not-recovered' },
    fingerprint: ['chunk-load-error'],
  })

  if (willReload) {
    try {
      sessionStorage.setItem('sc-chunk-reloaded', '1')
    } catch {
      /* sessionStorage unavailable — still reload */
    }
    reload()
  }
}

/**
 * Detect a failed dynamic import / stale-chunk error. After a deploy, a client running a
 * cached app shell can request a JS chunk whose hash no longer exists (404), which rejects
 * the lazy route import and otherwise leaves a blank page. Callers use this to reload once.
 */
export function isChunkLoadError(error: unknown): boolean {
  const msg = error instanceof Error ? `${error.name}: ${error.message}` : String(error)
  return (
    /ChunkLoadError/i.test(msg) ||
    /Loading chunk [\w-]+ failed/i.test(msg) ||
    /Failed to fetch dynamically imported module/i.test(msg) ||
    /error loading dynamically imported module/i.test(msg) ||
    /Importing a module script failed/i.test(msg)
  )
}
